import {
  motion,
  useAnimationControls,
  useMotionValue,
  animate as motionAnimate,
} from 'framer-motion';
import { useState, useRef, useEffect } from 'react';

export default function AnimatedButton({
  label,
  icon: Icon,
  onClick,
  bgColor = 'bg-customBlue',
  textColor = 'text-white',
  iconColor = 'text-customYellow',
  animateIcon = true,
  showGlow = true,
  pulseEffect = true,
  type = 'button',
  disabled = false,
}) {
  const buttonRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(0);
  const shineControls = useAnimationControls();
  const scale = useMotionValue(1);

  // Medir ancho del botón
  useEffect(() => {
    const measure = () => {
      if (buttonRef.current) setButtonWidth(buttonRef.current.offsetWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [label]);

  const shineWidth = Math.max(buttonWidth * 0.4, 40);

  // Loop sincronizado: Pulso + Shine
  useEffect(() => {
    if (!pulseEffect || !buttonWidth || disabled) return;

    let mounted = true;
    let currentAnimation = null;

    const timeout = setTimeout(() => {
      (async function loop() {
        while (mounted) {
          currentAnimation = motionAnimate(scale, 1.06, {
            duration: 0.9,
            ease: 'easeInOut',
          });
          await currentAnimation.finished;
          if (!mounted) break;

          if (showGlow) {
            shineControls.set({ x: 0 });
            shineControls
              .start({
                x: buttonWidth + shineWidth,
                transition: { duration: 1.2, ease: 'easeInOut' },
              })
              .catch(() => {});
          }

          currentAnimation = motionAnimate(scale, 1, {
            duration: 1.0,
            ease: 'easeInOut',
          });
          await currentAnimation.finished;
          if (!mounted) break;

          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      })();
    }, 0);

    return () => {
      mounted = false;
      clearTimeout(timeout);
      if (currentAnimation?.stop) currentAnimation.stop();
      shineControls.stop();
    };
  }, [
    buttonWidth,
    scale,
    pulseEffect,
    shineControls,
    shineWidth,
    showGlow,
    disabled,
  ]);

  return (
    <div ref={buttonRef} className="relative inline-block">
      {/* Fondo escalable */}
      <motion.div
        style={{ scale }}
        className={`absolute inset-0 rounded-full ${bgColor} shadow-[0px_10px_20px_rgba(0,0,0,0.2)] border-[3px] border-white/30`}
      />

      {/* Botón con contenido fijo */}
      <button
        type={type}
        disabled={disabled}
        onClick={onClick}
        className={`relative flex items-center gap-3 px-4 py-2 sm:px-5 sm:py-2 md:px-5 md:py-2 text-sm md:text-base font-bold font-heading rounded-full overflow-hidden z-10 ${textColor} ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <span>{label}</span>
        {Icon && (
          <motion.div
            className="flex items-center"
            animate={disabled ? {} : animateIcon ? { x: [0, 6, 0] } : {}}
            transition={
              disabled
                ? {}
                : animateIcon
                ? { repeat: Infinity, duration: 0.6, ease: 'easeInOut' }
                : {}
            }
          >
            <Icon className={`w-7 h-7 ${iconColor}`} />
          </motion.div>
        )}

        {showGlow && buttonWidth > 0 && !disabled && (
          <motion.div
            className="absolute top-0 h-full pointer-events-none"
            style={{
              left: -shineWidth,
              width: shineWidth,
              background:
                'linear-gradient(to right, transparent, rgba(255,255,255,0.7), transparent)',
              opacity: 0.6,
            }}
            animate={shineControls}
          />
        )}
      </button>
    </div>
  );
}
